/**
 * Derivación a otra especialidad. Cuando sale de dentro de una atención se
 * guarda `cita_origen_id`: así se sabe QUÉ consulta la generó, en vez de tener
 * derivaciones flotando en la ficha sin dueño.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createAsignacion, type AsignacionCreate } from "../../api/asignaciones";
import type { Cita } from "../../api/citas";
import { listEspecialidades, listProfesionales } from "../../api/profesionales";
import { listSedes } from "../../api/tenants";
import { useAuth } from "../../auth/AuthProvider";
import { useTenantBranding } from "../../auth/TenantBrandingProvider";
import { formatApiError } from "../../api/client";
import { Alert, Button, Dialog, Field } from "../ui";

export function DerivarDialog({
  pacienteId,
  open,
  onClose,
  citaOrigen,
}: {
  pacienteId: number;
  open: boolean;
  onClose: () => void;
  /** Consulta desde la que se deriva, si la derivación nace de una atención. */
  citaOrigen?: Cita | null;
}) {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const { branding } = useTenantBranding();
  const isSuperAdmin = !!me?.is_super_admin;
  const superAdminTenantId = isSuperAdmin ? branding?.id ?? null : null;

  const { data: especialidades = [] } = useQuery({
    queryKey: ["especialidades"],
    queryFn: listEspecialidades,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const { data: sedes = [] } = useQuery({
    queryKey: ["sedes", isSuperAdmin ? `tenant-${superAdminTenantId}` : "self"],
    queryFn: () =>
      isSuperAdmin && superAdminTenantId != null
        ? listSedes()
        : listSedes(),
    staleTime: 5 * 60_000,
    enabled: open && (!isSuperAdmin || superAdminTenantId != null),
  });
  const { data: profesionales = [] } = useQuery({
    queryKey: ["profesionales", "all"],
    queryFn: () => listProfesionales(),
    staleTime: 60_000,
    enabled: open,
  });

  const [form, setForm] = useState<AsignacionCreate>({
    paciente_id: pacienteId,
    especialidad_id: 0,
    profesional_id: null,
    sede_id: null,
    motivo: "",
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        paciente_id: pacienteId,
        cita_origen_id: citaOrigen?.id ?? null,
        especialidad_id: 0,
        profesional_id: null,
        // La derivación suele quedarse en la misma sede donde está el paciente.
        sede_id: citaOrigen?.sede_id ?? null,
        motivo: "",
      });
      setError(null);
    }
  }, [open, pacienteId, citaOrigen]);

  const mutation = useMutation({
    mutationFn: (payload: AsignacionCreate) => createAsignacion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["asignaciones", pacienteId] });
      queryClient.invalidateQueries({ queryKey: ["pacientes"] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
      if (citaOrigen)
        queryClient.invalidateQueries({ queryKey: ["cita", citaOrigen.id] });
      onClose();
    },
    onError: (err) => {
      setError(formatApiError(err, "No se pudo crear la derivación."));
    },
  });

  // Solo profesionales que cubren la especialidad elegida (y la sede, si la hay).
  const profesionalesFiltrados = profesionales.filter((p) => {
    if (!p.activo) return false;
    if (form.especialidad_id) {
      if (!p.especialidades.some((e) => e.id === form.especialidad_id)) return false;
    }
    if (form.sede_id) {
      if (!p.sedes.some((s) => s.id === form.sede_id)) return false;
    }
    return true;
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.especialidad_id) {
      setError("Selecciona la especialidad (área a la que se deriva).");
      return;
    }
    mutation.mutate({ ...form, motivo: form.motivo || null });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Derivar a otra especialidad"
      description={
        citaOrigen
          ? `Derivación que sale de la consulta de ${citaOrigen.especialidad_nombre}. Si no eliges profesional, queda en cola del área.`
          : "Deriva el paciente a una especialidad. Si no eliges profesional, queda en cola del área."
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field label="Especialidad / área *" htmlFor="a_esp">
          <select
            id="a_esp"
            value={form.especialidad_id || ""}
            onChange={(e) =>
              setForm({
                ...form,
                especialidad_id: Number(e.target.value) || 0,
                profesional_id: null,
              })
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="">— seleccionar —</option>
            {especialidades.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Sede (opcional)" htmlFor="a_sede">
          <select
            id="a_sede"
            value={form.sede_id ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                sede_id: e.target.value ? Number(e.target.value) : null,
                profesional_id: null,
              })
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">— cualquier sede —</option>
            {sedes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Profesional (opcional)"
          htmlFor="a_prof"
          hint="Si no eliges, el paciente queda en cola del área."
        >
          <select
            id="a_prof"
            value={form.profesional_id ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                profesional_id: e.target.value ? Number(e.target.value) : null,
              })
            }
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            disabled={!form.especialidad_id}
          >
            <option value="">— sin profesional (cola del área) —</option>
            {profesionalesFiltrados.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre_completo} ({p.tipo_colegio} {p.colegiatura})
              </option>
            ))}
          </select>
          {form.especialidad_id > 0 && profesionalesFiltrados.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              No hay profesionales que cubran esa especialidad
              {form.sede_id ? " en la sede seleccionada" : ""}.
            </p>
          )}
        </Field>

        <Field label="Motivo / nota" htmlFor="a_mot">
          <textarea
            id="a_mot"
            value={form.motivo ?? ""}
            onChange={(e) => setForm({ ...form, motivo: e.target.value })}
            maxLength={2000}
            rows={3}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Razón de la derivación, hallazgos relevantes, etc."
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Derivar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
